// src/components/WorkflowTimeline.js
import React, { useState, useEffect, useRef } from 'react';
import { useApi } from '../hooks/useApi';

// Action values written by the engine (backend/Models/RemediationWorkflowConstants.cs).
export const WORKFLOW_ACTION_LABELS = {
    enrolled: 'Workflow started',
    step_activated: 'Step started',
    step_completed: 'Step completed',
    step_reassigned: 'Step reassigned',
    workflow_completed: 'Workflow completed',
    workflow_cancelled: 'Workflow cancelled',
};

const ACTION_STYLE = {
    enrolled: { icon: '▶', dot: 'bg-indigo-500' },
    step_activated: { icon: '⚡', dot: 'bg-amber-500' },
    step_completed: { icon: '✅', dot: 'bg-green-500' },
    step_reassigned: { icon: '🔄', dot: 'bg-blue-500' },
    workflow_completed: { icon: '🏁', dot: 'bg-green-600' },
    workflow_cancelled: { icon: '⛔', dot: 'bg-red-500' },
};

export const workflowActionLabel = (action) => WORKFLOW_ACTION_LABELS[action] || action || 'Workflow event';

// Backend returns UTC datetimes without a 'Z' suffix; treat them as UTC (same as ActivityLogTab).
const parseAsUtc = (dateStr) => {
    if (!dateStr) return null;
    const s = String(dateStr);
    return new Date(s.endsWith('Z') || s.includes('+') || s.includes('-', 10) ? s : s + 'Z');
};

const formatTime = (dateStr) => {
    const d = parseAsUtc(dateStr);
    return d ? d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '';
};

const formatDateLabel = (dateStr) => {
    const d = parseAsUtc(dateStr);
    return d ? d.toLocaleDateString('en-US', { month: 'short', day: '2-digit' }) : '';
};

const bold = (val) => <span className="font-semibold text-gray-800">{val}</span>;

/**
 * Step-level audit trail of a risk's remediation workflows (custom workflows Phase 0).
 * Loads `GET /remediation-workflows/activity/by-risk/{riskId}` (newest first; read gated by the
 * backend's risk-read check) and renders it in the ActivityLogTab style. Bump `refreshKey`
 * to reload after the step panel changes something. `onLoaded()` fires exactly once, after the
 * first load settles (any outcome), so a parent can gate the section's visibility.
 */
const WorkflowTimeline = ({ riskId, refreshKey = 0, onLoaded }) => {
    const apiCall = useApi();
    // The api hook is re-created per render; keep it (and the callback) in refs so the effect
    // depends on inputs only.
    const apiRef = useRef(apiCall);
    const onLoadedRef = useRef(onLoaded);
    const loadedOnceRef = useRef(false);
    useEffect(() => { apiRef.current = apiCall; onLoadedRef.current = onLoaded; });

    const reportLoaded = () => {
        if (loadedOnceRef.current) return;
        loadedOnceRef.current = true;
        if (onLoadedRef.current) onLoadedRef.current();
    };

    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!riskId) {
            setEntries([]);
            setLoading(false);
            reportLoaded();
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        (async () => {
            try {
                const res = await apiRef.current('GET', `/remediation-workflows/activity/by-risk/${encodeURIComponent(riskId)}`);
                if (!cancelled) setEntries(Array.isArray(res) ? res : []);
            } catch (err) {
                if (!cancelled) setError(err?.response?.data?.message || 'The workflow history could not be loaded.');
            } finally {
                if (!cancelled) {
                    setLoading(false);
                    reportLoaded();
                }
            }
        })();
        return () => { cancelled = true; };
        // reportLoaded only touches refs.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [riskId, refreshKey]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-6" data-testid="workflow-timeline-loading">
                <p className="text-sm text-gray-500">Loading workflow history...</p>
            </div>
        );
    }

    if (error) {
        return <div className="text-sm text-red-600" role="alert">{error}</div>;
    }

    if (entries.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-6 text-center" data-testid="workflow-timeline-empty">
                <p className="text-sm font-medium text-gray-500">No workflow activity yet</p>
                <p className="text-xs text-gray-400 mt-1">Steps will appear here as the workflow progresses</p>
            </div>
        );
    }

    const renderSummary = (entry) => {
        const actor = entry.actorName || (entry.actorUserId != null ? `User #${entry.actorUserId}` : 'System');
        const hasTransition = entry.previousValue || entry.newValue;
        return (
            <>
                {bold(workflowActionLabel(entry.action))}
                {entry.stepName && <>: {bold(entry.stepName)}</>}
                {hasTransition && (
                    <span className="ml-1 text-gray-500">
                        ({entry.previousValue ? <>{entry.previousValue} → </> : null}{entry.newValue})
                    </span>
                )}
                {' '}by {bold(actor)}
            </>
        );
    };

    return (
        <div className="relative" data-testid="workflow-timeline">
            {/* Vertical line */}
            <div className="absolute left-[4.75rem] top-3 bottom-3 w-px bg-gray-200 pointer-events-none" />

            {entries.map((entry, idx) => {
                const style = ACTION_STYLE[entry.action] || ACTION_STYLE.step_activated;
                const dateLabel = formatDateLabel(entry.createdAt);
                const prevDateLabel = idx > 0 ? formatDateLabel(entries[idx - 1].createdAt) : null;
                const showDateSep = idx === 0 || dateLabel !== prevDateLabel;

                return (
                    <div key={entry.id ?? idx} data-testid="workflow-timeline-row">
                        {showDateSep && (
                            <div className={`flex items-center ${idx > 0 ? 'mt-4' : ''} mb-2`}>
                                <div className="w-[4.25rem] flex-shrink-0 pr-2 text-right">
                                    <span className="text-xs font-bold text-gray-500 whitespace-nowrap">{dateLabel}</span>
                                </div>
                                <div className="w-8 flex-shrink-0" />
                                <div className="flex-1 h-px bg-gray-200" />
                            </div>
                        )}
                        <div className="flex items-center mb-2">
                            <div className="w-[4.25rem] flex-shrink-0 text-right pr-2">
                                <span className="text-[11px] text-gray-400 whitespace-nowrap">{formatTime(entry.createdAt)}</span>
                            </div>
                            <div className="w-8 flex-shrink-0 flex justify-center relative z-10">
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] ${style.dot}`}>
                                    {style.icon}
                                </div>
                            </div>
                            <div className="flex-1 min-w-0 ml-2">
                                <p className="text-xs text-gray-600 leading-snug">{renderSummary(entry)}</p>
                                {entry.comment && (
                                    <p className="text-[11px] text-gray-400 italic mt-0.5">"{entry.comment}"</p>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default WorkflowTimeline;
